import React, {useContext, useEffect, useState} from "react";
import {
  Avatar,
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import {formatEuroValues} from "../../../../common/methods/utils";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {PrimaryBigButton, SecondaryBigButton} from "../../../../components/buttons/mainButton";
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import {IconPosition} from "../../../../components/buttons/buttonProps";
import {TOperationParticipant} from "../../../../interfaces/operationParticipant";
import {TOperation} from "../../../../interfaces/operation";
import {TParticipant} from "../../../../interfaces/participant";
import {StoreContext} from "../../../../common/struct/store";
import {AppContext} from "../../../../App";
import {WalletSelector} from "./pointsUtils";

type AddPointsToParticipantProps = {
  openDialog: boolean,
  sendMoney: (amount: number, wallet: string) => void,
  closeDialog: () => void,
  operationParticipant: TOperationParticipant|null,
  operation?: TOperation
}

export default function AddPointsToParticipant(props: AddPointsToParticipantProps): JSX.Element {
  const {openDialog, sendMoney, closeDialog, operationParticipant, operation} = props;
  const [store] = useContext<StoreContext>(AppContext);
  const [pointValue, setPointValue] = useState<number>(1);
  const [selectedWallet, setSelectedWallet] = useState<string|undefined>(store.global.company.defaultWallet ? store.global.company.defaultWallet['@id']: undefined);
  const [disabledConfirm, setDisabledConfirm] = useState<boolean>(false);
  const displayWalletSelector = store.global.company.wallets.length > 2;

  const {t} = useTranslation();

  const participantName = (participant: TParticipant): string => {
    const names = [participant.firstName??'', participant.lastName??''];
    return names.join(' ');
  }

  const onConfirmMoney = (): void => {
    if (operation == null) {
      return;
    }

    closeDialog();   
    
    sendMoney(store.global.company.euroPerPoint * pointValue, selectedWallet ? selectedWallet : store.global.company.defaultWallet ? store.global.company.defaultWallet['@id']: 'none');
  }

  useEffect(() => {
    if (store.global.company.wallets.length > 2) {
      store.global.company.wallets.map((wallet) => {
        if ((selectedWallet == wallet['@id'])) {                
          if ((pointValue*store.global.company.euroPerPoint) > (wallet.balance ?? 0)) {
            setDisabledConfirm(true);
          } else {
            setDisabledConfirm(false);
          }
        }
      })
    }
  }, [selectedWallet, pointValue])

  return (operationParticipant == null || operation == null) ? <></> :
    <Dialog open={openDialog} onClose={closeDialog} sx={{display: "flex", justifyContent: "center"}}>
      <DialogTitle>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb:5}}>
          <Typography color="neutral.dark" sx={{fontWeight: "bold"}} variant="h2">{(operationParticipant.participant.firstName || operationParticipant.participant.lastName) ? t('participants.give_money_to_participant.title', {name: participantName(operationParticipant.participant)}) : t('participants.give_money_to_participant.sub_title')}</Typography>
          <IconButton onClick={closeDialog}><CloseIcon/></IconButton>
        </Box>
        <Card sx={{display: 'flex', flexDirection:'row', alignItems:'center', my:3, p:3, backgroundColor: 'ornament.main'}}>
          <Avatar
            sx={{height: "24px", width: "24px", backgroundColor: "ornament.dark", p:1, mr:2}}
          >
            <Typography color="neutral.dark" variant="body2" sx={{fontWeight: "bold"}}>
              {operationParticipant.participant.firstName && operationParticipant.participant.firstName.charAt(0).toUpperCase()}
              {operationParticipant.participant.lastName && operationParticipant.participant.lastName.charAt(0).toUpperCase()}
            </Typography>
          </Avatar>
          <Typography color="neutral.main" sx={{fontWeight: "medium", pr: 3, borderRight: "1px solid", borderColor: "ornament.dark"}} variant="body1">
            {participantName(operationParticipant.participant)}
          </Typography>
          <Typography color="neutral.main" sx={{fontWeight: "medium", pl: 3}} variant="body1">{t('participants.give_money_to_participant.sold_text')}</Typography>
          <Typography color="neutral.dark" sx={{fontWeight: "bold", ml:10}} variant="body1">{t('participants.give_money_to_participant.sold_value', {pointsValue: operationParticipant.rewardPoints})}</Typography>
        </Card>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onConfirmMoney} >
          <Typography color="neutral.dark" sx={{fontWeight: "bold", mb:2}} variant="body1">{t('participants.give_money_to_participant.sub_title')}</Typography>
          <TextField onChange={(event: any): void => !isNaN(event.target.value) ? setPointValue(Math.abs(event.target.value)): setPointValue(0)} type="string" color="info" value={pointValue}
            sx={{width: '100%', mr: 2, "& input": {p: 3}}}
            InputProps={{
              lang: "fr-FR",
              endAdornment: <InputAdornment position="end"><Typography color="neutral.dark" sx={{fontWeight: "regular"}} variant="body2">{t('participants.give_money_to_participant.unit')}</Typography></InputAdornment>,
              inputProps: {
                min: 1,
              }
            }}
          />
          {displayWalletSelector &&
            <WalletSelector selectedWallet={selectedWallet} setSelectedWallet={setSelectedWallet}/>
          }
        </form>
        <Box sx={{display: 'flex', alignItems: 'center', mt:2}}>
          <InfoOutlinedIcon sx={{color: 'info.main', height: "15px", mr: 0}}/>
          <Typography color="info.main" sx={{fontWeight: "regular"}} variant="body1">{t('participants.give_money_to_participant.content', {euroValue: formatEuroValues(store.global.company.euroPerPoint*pointValue)})}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{display: 'flex', justifyContent: 'right', borderTop: "1px solid", borderColor: "ornament.dark", p: 5}}>
        <SecondaryBigButton
          label={t('participants.give_money_to_participant.cancel_button')}
          action={closeDialog}
        />
        <PrimaryBigButton
          label={t('participants.give_money_to_participant.confirm_button')}
          icon={AddOutlinedIcon}
          position={IconPosition.LEFT}
          action={onConfirmMoney}
          disabled={disabledConfirm}
        />
      </DialogActions>
    </Dialog>
}
